// Navbar.js
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { FaTimes, FaBars, FaSun, FaMoon } from 'react-icons/fa';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      setIsDarkMode(savedTheme === 'dark');
      document.documentElement.setAttribute('data-theme', savedTheme);
    } else {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setIsDarkMode(prefersDark);
      document.documentElement.setAttribute('data-theme', prefersDark ? 'dark' : 'light');
    }
  }, []);

  const toggleTheme = () => {
    const newTheme = isDarkMode ? 'light' : 'dark';
    setIsDarkMode(!isDarkMode);
    document.documentElement.setAttribute('data-theme', newTheme);
    localStorage.setItem('theme', newTheme);
  };

  const toggleMenu = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    console.log('Menu toggled, isOpen:', newState);
  };

  const handleBackClick = () => {
    const fromPrivacy = sessionStorage.getItem('fromPrivacy');
    if (fromPrivacy) {
      sessionStorage.removeItem('fromPrivacy');
      navigate(-1);
    } else {
      navigate(-1);
    }
  };

  // Close menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const isHomePage = location.pathname === '/';

  return (
    <>
      <Nav>
        {/* {!isHomePage && <BackButton onClick={handleBackClick}>←</BackButton>} */}

        {/* ✅ LOGO LEFT */}
        <LogoContainer>
          <Logo to="/" onClick={() => setIsOpen(false)}>
            <img
              src="/images/shared/innit-logo.png"
              alt="Innit Logo"
              loading="eager"
            />
          </Logo>
        </LogoContainer>

        {/* ✅ MENU RIGHT */}
        <DesktopMenu>
          <MenuItem to="/">Home</MenuItem>
          <MenuItem to="/employers">For Employers</MenuItem>
          <MenuItem to="/employees">For Employees</MenuItem>
          <MenuItem to="/about-us">About Us</MenuItem>
          <MenuItem to="/contact">Contact Us</MenuItem>
          <ThemeToggle onClick={toggleTheme} title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
            {isDarkMode ? <FaSun size={18} /> : <FaMoon size={18} />}
          </ThemeToggle>
        </DesktopMenu>

        {/* ✅ HAMBURGER ICON */}
        <Hamburger onClick={toggleMenu} aria-label="Toggle menu">
          {isOpen ? <FaTimes /> : <FaBars />}
        </Hamburger>
      </Nav>

      {/* ✅ MOBILE MENU - Rendered outside Nav using Portal */}
      {isOpen && typeof document !== 'undefined' && createPortal(
        <MobileMenuOverlay
          $isDarkMode={isDarkMode}
        >
          <CloseButton onClick={toggleMenu}>
            <FaTimes size={24} />
          </CloseButton>

          <MobileLogoContainer $isDarkMode={isDarkMode}>
            <Logo to="/" onClick={() => setIsOpen(false)}>
              <img
                src="/images/shared/innit-logo.png"
                alt="Innit Logo"
              />
            </Logo>
          </MobileLogoContainer>

          <MobileMenuItems>
            <MobileNavItem
              onClick={() => {
                setIsOpen(false);
                navigate('/');
              }}
              $isActive={location.pathname === '/'}
            >
              Home
            </MobileNavItem>
            <MobileNavItem
              onClick={() => {
                setIsOpen(false);
                navigate('/employers');
              }}
              $isActive={location.pathname === '/employers'}
            >
              For Employers
            </MobileNavItem>
            <MobileNavItem
              onClick={() => {
                setIsOpen(false);
                navigate('/employees');
              }}
              $isActive={location.pathname === '/employees'}
            >
              For Employees
            </MobileNavItem>
            <MobileNavItem
              onClick={() => {
                setIsOpen(false);
                navigate('/about-us');
              }}
              $isActive={location.pathname === '/about-us'}
            >
              About Us
            </MobileNavItem>
            <MobileNavItem
              onClick={() => {
                setIsOpen(false);
                navigate('/contact');
              }}
              $isActive={location.pathname === '/contact'}
            >
              Contact Us
            </MobileNavItem>
            <MobileThemeToggle
              onClick={toggleTheme}
            >
              {isDarkMode ? <><FaSun /> Switch to Light Mode</> : <><FaMoon /> Switch to Dark Mode</>}
            </MobileThemeToggle>
          </MobileMenuItems>
        </MobileMenuOverlay>,
        document.body
      )}
    </>
  );
};

export default Navbar;

/* ---------------- STYLED COMPONENTS ---------------- */

const Nav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  
  /* Ensure mobile menu appears above navbar */
  & > div[style*="zIndex: 10000"] {
    z-index: 10000 !important;
  }
  padding: 0.75rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--nav-bg);
  border-bottom: 1px solid var(--nav-border);
  box-shadow: var(--shadow-sm);
  transition: all 0.3s ease;
  height: 70px;
  overflow: visible;
  backdrop-filter: blur(10px);
  box-sizing: border-box;

  @media (max-width: 900px) {
    padding: 0.6rem 1rem 0.6rem 1.5rem;
    height: 65px;
  }

  @media (max-width: 768px) {
    padding: 0.5rem 0.75rem 0.5rem 1rem;
    height: 60px;
  }

  @media (max-width: 480px) {
    padding: 0.5rem 0.5rem 0.5rem 0.75rem;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-left: 0;
  height: 100%;
  flex: 1 1 auto;
  z-index: 1002;
  min-width: 0;

  @media (max-width: 900px) {
    flex: 0 1 auto;
    max-width: calc(100% - 60px);
  }

  @media (max-width: 768px) {
    max-width: calc(100% - 55px);
  }

  img {
    height: 42px;
    width: auto;
    object-fit: contain;
    background-color: transparent;
    border-radius: 0;
    box-shadow: none;
    transition: all 0.3s ease;
    max-height: 100%;
    display: block;
    opacity: 0.9;

    &:hover {
      opacity: 1;
      transform: scale(1.02);
    }

    @media (max-width: 900px) {
      height: 38px;
    }

    @media (max-width: 768px) {
      height: 35px;
    }

    @media (max-width: 480px) {
      height: 32px;
    }
  }
`;

const Logo = styled(RouterLink)`
  display: flex;
  align-items: center;
`;

const DesktopMenu = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2.5rem;
  flex: 1;
  max-width: 1150px;
  margin: 0 auto;
  padding-right: 2rem;

  @media (max-width: 1024px) {
    gap: 2rem;
    padding-right: 1.5rem;
  }

  @media (max-width: 900px) {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
    overflow: hidden !important;
    padding: 0 !important;
    margin: 0 !important;
    flex: 0 0 0 !important;
  }
`;

const MenuItem = styled(RouterLink)`
  color: var(--text-primary);
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  letter-spacing: 0.2px;
  transition: all 0.3s ease;
  position: relative;
  padding: 0.5rem 0.75rem;
  display: inline-block;
  line-height: 1.4;
  white-space: nowrap;
  margin: 0;
  border-radius: 4px;

  &:hover {
    color: var(--primary-blue);
    background: rgba(51, 193, 255, 0.1);
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0.25rem;
    left: 0.75rem;
    width: 0;
    height: 2px;
    background-color: var(--primary-blue);
    transition: width 0.3s ease;
    border-radius: 2px;
  }

  &:hover::after {
    width: calc(100% - 1.5rem);
  }
`;

const ThemeToggle = styled.button`
  background: transparent;
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  font-weight: 500;
  gap: 0.5rem;

  &:hover {
    background: rgba(51, 193, 255, 0.1);
    border-color: var(--primary-blue);
    color: var(--primary-blue);
  }
`;

const Hamburger = styled.button`
  display: none;
  cursor: pointer;
  position: relative;
  z-index: 1001;
  background: transparent;
  border: none;
  padding: 8px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;
  color: var(--text-primary);
  transition: all 0.3s ease;
  margin: 0;
  
  svg {
    width: 24px;
    height: 24px;
    display: block;
    flex-shrink: 0;
  }

  &:hover {
    color: var(--primary-blue);
    background: rgba(51, 193, 255, 0.1);
    border-radius: 4px;
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 900px) {
    display: flex !important;
    margin-right: 0;
    padding: 8px;
  }

  @media (max-width: 768px) {
    margin-right: 0;
    padding: 8px;
  }

  @media (max-width: 480px) {
    margin-right: 0;
    padding: 8px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  cursor: pointer;
  z-index: 10001;
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  transition: all 0.3s ease;
  min-width: 44px;
  min-height: 44px;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.5);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const MenuItemsContainer = styled.div`
  &&& {
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    justify-content: center !important;
    width: 100% !important;
    gap: 1rem !important;
    margin-top: 2rem !important;
    padding: 0 20px !important;
    position: relative !important;
    z-index: 10000 !important;
    opacity: 1 !important;
    visibility: visible !important;
    flex: 1 !important;
  }
`;

const MobileMenuOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: ${props => props.$isDarkMode 
    ? 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)' 
    : 'linear-gradient(135deg, var(--primary-blue) 0%, var(--primary-blue-dark) 100%)'};
  z-index: 10000;
  padding: 0;
  overflow-y: auto;
  overflow-x: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const MobileLogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 3rem 20px 2rem 20px;
  margin-top: 60px;

  img {
    height: 80px;
    width: auto;
    display: block;
    margin: 0 auto;
    filter: ${props => props.$isDarkMode ? 'none' : 'brightness(0) invert(1)'};
    transition: transform 0.3s ease;
  }

  img:hover {
    transform: scale(1.05);
  }

  @media (max-width: 480px) {
    padding: 2.5rem 15px 1.5rem 15px;
    margin-top: 50px;
    
    img {
      height: 70px;
    }
  }
`;

const MobileMenuItems = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  max-width: 500px;
  gap: 1rem;
  padding: 0 20px 2rem 20px;
  flex: 1;
`;

const MobileNavItem = styled.button`
  width: 100%;
  padding: 1.25rem 1.5rem;
  background: ${props => props.$isActive 
    ? 'rgba(255, 255, 255, 0.2)' 
    : 'rgba(255, 255, 255, 0.08)'};
  border: 2px solid ${props => props.$isActive 
    ? 'rgba(255, 255, 255, 0.4)' 
    : 'rgba(255, 255, 255, 0.25)'};
  border-radius: 12px;
  color: #ffffff;
  font-size: 1.05rem;
  font-weight: 500;
  font-family: 'Poppins', sans-serif;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  display: block;
  margin: 0;
  box-sizing: border-box;
  box-shadow: ${props => props.$isActive 
    ? '0 4px 12px rgba(0, 0, 0, 0.15)' 
    : 'none'};

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.4);
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 480px) {
    padding: 1.1rem 1.25rem;
    font-size: 1rem;
    border-radius: 10px;
  }
`;

const MobileThemeToggle = styled.button`
  width: 100%;
  padding: 1.25rem 1.5rem;
  background: rgba(255, 255, 255, 0.08);
  border: 2px solid rgba(255, 255, 255, 0.25);
  border-radius: 12px;
  color: #ffffff;
  font-size: 1.05rem;
  font-weight: 500;
  font-family: 'Poppins', sans-serif;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin: 0.5rem 0 0 0;
  box-shadow: none;
  box-sizing: border-box;

  svg {
    font-size: 1.1rem;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.4);
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 480px) {
    padding: 1.1rem 1.25rem;
    font-size: 1rem;
    border-radius: 10px;
    gap: 0.6rem;
  }
`;

const MobileMenu = styled.div`
  &&& {
    display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')} !important;
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    height: 100vh !important;
    background: linear-gradient(135deg, #1a2332 0%, #2d3748 100%) !important;
    z-index: 9999 !important;
    padding-top: 100px !important;
    padding-bottom: 2rem !important;
    overflow-y: auto !important;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3) !important;
    pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')} !important;
  }

  ${Logo} {
    margin-bottom: 3rem !important;
    display: block !important;
    opacity: 1 !important;
    visibility: visible !important;
    z-index: 10001 !important;
    
    img {
      height: 90px !important;
      width: auto !important;
      filter: brightness(0) invert(1) !important;
      display: block !important;
      opacity: 1 !important;
      visibility: visible !important;
    }
  }

  ${MenuItemsContainer} {
    display: flex !important;
    flex-direction: column !important;
    opacity: 1 !important;
    visibility: visible !important;
    z-index: 10001 !important;
    width: 100% !important;
  }
`;

const BackButton = styled.button`
  position: absolute;
  left: 15px;
  top: 16px;
  background: none;
  border: none;
  font-size: 2.5rem;
  color: white;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;